import React, { useCallback, useEffect, useState } from 'react';
import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { useLDClient, useLDClientError } from 'launchdarkly-react-client-sdk';

import LoadingPage from '../../../components/layout/Page/LoadingPage';
import { Auth0AuthContextProvider } from './Auth0AuthContextProvider';
import { DigletAuthContextProvider } from './DigletAuthContextProvider';
import { POST_LOGIN_REDIRECT_COOKIE } from 'modules/shared/constants/auth';
import routes from 'routes';
import { AUTH0_AUDIENCE, AUTH0_CLIENT_ID, AUTH0_DOMAIN, getAuth0RedirectUri, getSafeReturnTo } from 'utils/auth0';
import CookieUtils from 'utils/cookies';
import { FLAGS, useFeatureFlag } from 'utils/launchdarkly/flags';

// How long to wait for LaunchDarkly before falling back to the flag's default (Diglet sign-in), so
// an unreachable LaunchDarkly cannot hold every page on the loading screen.
export const LAUNCH_DARKLY_READY_TIMEOUT_MS = 5000;

/**
 * AuthContextProvider picks how this session signs in, from the `auth0_admin_portal_login_2026_temp`
 * flag: Auth0 when it is on, Diglet when it is off.
 *
 * Hosted LaunchDarkly loads flags after the first render, so the flag reads its default until the
 * client is ready. The choice therefore waits for LaunchDarkly (or the timeout above) and is then
 * kept for the life of the page: flipping the flag mid-session must not swap the auth system out
 * from under a signed-in user. Pages that differ by sign-in method read `isAuth0Login` from
 * AuthContext rather than the flag, so they always agree with this choice.
 */
export const AuthContextProvider = ({ children }: React.PropsWithChildren<object>) => {
  const ldClient = useLDClient();
  const ldClientError = useLDClientError();
  const isAuth0LoginFlagOn = useFeatureFlag(FLAGS.AUTH0_ADMIN_PORTAL_LOGIN) === true;

  const [isLaunchDarklyTimedOut, setIsLaunchDarklyTimedOut] = useState(false);
  const [isAuth0Login, setIsAuth0Login] = useState<boolean>();
  const [postSignInPath, setPostSignInPath] = useState<string>();

  const isLaunchDarklySettled = Boolean(ldClient || ldClientError) || isLaunchDarklyTimedOut;

  useEffect(() => {
    if (isLaunchDarklySettled) {
      return;
    }

    const timeoutId = setTimeout(() => setIsLaunchDarklyTimedOut(true), LAUNCH_DARKLY_READY_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isLaunchDarklySettled]);

  useEffect(() => {
    if (isLaunchDarklySettled && isAuth0Login === undefined) {
      setIsAuth0Login(isAuth0LoginFlagOn);
    }
  }, [isAuth0Login, isAuth0LoginFlagOn, isLaunchDarklySettled]);

  // Runs when Universal Login sends the user back to `/`, before the session is hydrated. The page
  // to restore is the one the user opened (see Auth0SignInButton), or Home: outside production `/`
  // is the Magic ENV page, which would not send a signed-in user on.
  const onAuth0RedirectCallback = useCallback((appState?: AppState) => {
    // As the SDK's default callback does: take the single-use code and state out of the URL
    window.history.replaceState({}, document.title, window.location.pathname);
    CookieUtils.remove(POST_LOGIN_REDIRECT_COOKIE);
    setPostSignInPath(getSafeReturnTo(appState?.returnTo) ?? routes.Home.as);
  }, []);

  if (isAuth0Login === undefined) {
    return <LoadingPage />;
  }

  if (!isAuth0Login) {
    return <DigletAuthContextProvider>{children}</DigletAuthContextProvider>;
  }

  // Refresh tokens let the SDK renew access tokens with a CORS request to Auth0, instead of the
  // hidden iframe that needs Auth0's session cookie. Safari, Brave and other browsers that block
  // third-party cookies fail the iframe, which would log users out when the token expires. The
  // refresh token is held in memory, so after a page reload the SDK falls back to the iframe.
  // Keep the default in-memory cache: in localStorage, an XSS bug could steal the refresh token.
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{ redirect_uri: getAuth0RedirectUri(), audience: AUTH0_AUDIENCE }}
      useRefreshTokens
      useRefreshTokensFallback
      onRedirectCallback={onAuth0RedirectCallback}
    >
      <Auth0AuthContextProvider postSignInPath={postSignInPath}>{children}</Auth0AuthContextProvider>
    </Auth0Provider>
  );
};
